/**
 * O3 (Salon Owner localization & polish) — owner-portal strings for the
 * "status" area (status system, calendar cards / legend / week view, Calendar
 * settings). Merged into the runtime dictionary by i18n/owner/index.ts and read
 * through LanguageContext's t(). Every key needs BOTH an en and an ar value.
 */
import type { OwnerStrings } from './types';

export const statusStrings: OwnerStrings = {
    en: {
        'qbr.fb.staff': 'the staff member',
        'qbr.fb.service': 'this service',
        'qbr.fb.branch': 'this branch',
        'qbr.fb.time': 'that time',
        'qbr.branchClosed.toast': 'The branch is closed on this day.',
        'qbr.branchClosed.emptyTitle': '{branch} is closed on this day.',
        'qbr.branchClosed.emptyHint': 'Pick another date. To change opening hours, go to My Business → Business details → Hours.',
        'qbr.outsideShift.toast': '{staff} isn’t working at that time.',
        'qbr.outsideShift.banner': '{staff} isn’t working around {time}. Showing other staff at {branch} who offer {service}.',
        'qbr.outsideShift.emptyTitle': 'Nobody at {branch} is working around {time} on this day.',
        'qbr.outsideShift.emptyHint': 'Try another time or date, or set working hours in My Business → Manage staff → Schedule.',
        'qbr.staffNotAtBranch.toast': '{staff} doesn’t work at {branch}.',
        'qbr.staffNotAtBranch.banner': '{staff} doesn’t work at {branch}. Showing other staff at this branch who offer {service}.',
        'qbr.staffNotAtBranch.emptyTitle': 'No staff at {branch} offer {service}.',
        'qbr.staffNotAtBranch.emptyHint': 'Add a staff member to this branch in My Business → Manage staff, or give the service to another staff member.',
        'qbr.staffCantService.toast': '{staff} doesn’t offer {service}.',
        'qbr.staffCantService.banner': '{staff} doesn’t offer {service}. Showing other staff at {branch} who do.',
        'qbr.staffCantService.emptyTitle': 'No staff at {branch} offer {service}.',
        'qbr.staffCantService.emptyHint': 'Give the service to a staff member in My Business → Manage staff → Edit staff, then come back.',
        'qbr.genderMismatch.toast': '{staff} can’t provide {service} because of its gender setting.',
        'qbr.genderMismatch.banner': '{service} has a gender setting {staff} doesn’t match. Showing other staff at {branch} who do.',
        'qbr.genderMismatch.emptyTitle': 'No staff at {branch} match the gender setting of {service}.',
        'qbr.genderMismatch.emptyHint': 'Change the setting in My Business → Services → {service}, or add staff who match.',
        'qbr.conflict.toast': 'That time was just booked. Please pick another.',
        'qbr.conflict.emptyTitle': '{staff} has no free time for {service} around {time}.',
        'qbr.conflict.emptyHint': 'Try another time, or pick another staff member.',
        'qbr.noPricing.toast': '{service} has no price yet. Add a price in My Business → Services first.',
        'qbr.noPricing.emptyTitle': '{service} has no price yet.',
        'qbr.noPricing.emptyHint': 'Add at least one price in My Business → Services → Edit, then come back.',
        'qbr.inPast.toast': 'You can’t book a time that has passed. Pick a current or future time.',
        'qbr.inPast.emptyTitle': 'No times available on past dates.',
        'qbr.inPast.emptyHint': 'Pick today or a future date.',
        'qbr.unknown.toast': 'We couldn’t make this booking. Please try again.',
        'qbr.unknown.emptyTitle': 'No available times match your choices.',
        'qbr.unknown.emptyHint': 'Try another date or time.',
        'cal.legend.label': 'Booking statuses',
        'cal.legend.mobileHint': '📱 = booked in the Devanza app',
        'cal.week.bookAt': 'Book at {time}',
        'cal.week.showAll': 'Show all {count} bookings at this time',
        'cal.week.more': '+{count} more',
        'cal.week.allAt': 'All {count} bookings at {time}',
        'cal.week.oneBooking': '1 booking',
        'cal.week.nBookings': '{count} bookings',
        'cal.week.close': 'Close',
        'cal.week.bookNewAt': '+ New booking at {time}',
        'cs.title': 'Calendar settings',
        'cs.subtitle': 'Choose how your calendar works and which booking statuses your salon uses.',
        'cs.loadError': 'We couldn’t load your calendar settings.',
        'cs.retry': 'Try again',
        'cs.time.title': 'Time settings',
        'cs.time.subtitle': 'How time slots and bookings work on your calendar.',
        'cs.overlap.label': 'Allow double bookings',
        'cs.overlap.help': 'When on, you can book two appointments for the same staff member at the same time (for example, a mother and daughter together). When off, a booking that overlaps another is not allowed.',
        'cs.backdating.label': 'Allow bookings in the past',
        'cs.backdating.help': 'When on, you can add a booking for a time that has already passed, for example to record a walk-in afterwards. When off, only current and future times can be booked.',
        'cs.weekStart': 'Week starts on',
        'cs.day.sunday': 'Sunday',
        'cs.day.monday': 'Monday',
        'cs.day.saturday': 'Saturday',
        'cs.timeSlot': 'Time slot length',
        'cs.minutes': '{n} min',
        'cs.timeFormat': 'Time format',
        'cs.timeFormat.12': '12-hour (2:30 PM)',
        'cs.timeFormat.24': '24-hour (14:30)',
        'cs.status.title': 'Booking statuses',
        'cs.status.subtitle': 'Turn off the steps your salon doesn’t track. Status names and colours are the same across Devanza so everyone reads bookings the same way.',
        'cs.status.hint.booked': 'New booking requests waiting for you, and bookings you have confirmed.',
        'cs.status.hint.arrived': 'The customer has arrived at the salon.',
        'cs.status.hint.started': 'The service is in progress.',
        'cs.status.hint.completed': 'The visit is finished.',
        'cs.status.hint.noShow': 'The customer didn’t come.',
        'cs.status.hint.cancelled': 'The booking was cancelled or declined.',
        'cs.status.alwaysOn': 'Always on.',
        'cs.status.note': 'Turning a status off hides it from the actions you can take on new bookings. Bookings already in that status keep it and still show on your calendar.',
        'cs.discard': 'Discard changes',
        'cs.save': 'Save settings',
        'cs.saving': 'Saving…',
        'cs.toast.saved': 'Calendar settings saved.',
        'cs.toast.saveFailed': 'We couldn’t save your calendar settings. Please try again.',
    },
    ar: {
        'qbr.fb.staff': 'الموظف',
        'qbr.fb.service': 'هذه الخدمة',
        'qbr.fb.branch': 'هذا الفرع',
        'qbr.fb.time': 'ذلك الوقت',
        'qbr.branchClosed.toast': 'الفرع مغلق في هذا اليوم.',
        'qbr.branchClosed.emptyTitle': '{branch} مغلق في هذا اليوم.',
        'qbr.branchClosed.emptyHint': 'اختر تاريخًا آخر. لتغيير ساعات العمل، انتقل إلى «منشأتي» ← «تفاصيل المنشأة» ← «ساعات العمل».',
        'qbr.outsideShift.toast': '{staff} لا يعمل في ذلك الوقت.',
        'qbr.outsideShift.banner': '{staff} لا يعمل في حدود الساعة {time}. نعرض موظفين آخرين في {branch} يقدّمون {service}.',
        'qbr.outsideShift.emptyTitle': 'لا يوجد أحد في {branch} يعمل في حدود الساعة {time} في هذا اليوم.',
        'qbr.outsideShift.emptyHint': 'جرّب وقتًا أو تاريخًا آخر، أو حدّد ساعات العمل من «منشأتي» ← «إدارة الموظفين» ← «الجدول».',
        'qbr.staffNotAtBranch.toast': '{staff} لا يعمل في {branch}.',
        'qbr.staffNotAtBranch.banner': '{staff} لا يعمل في {branch}. نعرض موظفين آخرين في هذا الفرع يقدّمون {service}.',
        'qbr.staffNotAtBranch.emptyTitle': 'لا يوجد موظف في {branch} يقدّم {service}.',
        'qbr.staffNotAtBranch.emptyHint': 'أضف موظفًا إلى هذا الفرع من «منشأتي» ← «إدارة الموظفين»، أو أسند الخدمة إلى موظف آخر.',
        'qbr.staffCantService.toast': '{staff} لا يقدّم {service}.',
        'qbr.staffCantService.banner': '{staff} لا يقدّم {service}. نعرض موظفين آخرين في {branch} يقدّمونها.',
        'qbr.staffCantService.emptyTitle': 'لا يوجد موظف في {branch} يقدّم {service}.',
        'qbr.staffCantService.emptyHint': 'أسند الخدمة إلى موظف من «منشأتي» ← «إدارة الموظفين» ← «الخدمات»، ثم عُد إلى هنا.',
        'qbr.genderMismatch.toast': 'لا يمكن لـ{staff} تقديم {service} بسبب إعداد الجنس الخاص بها.',
        'qbr.genderMismatch.banner': 'إعداد الجنس في {service} لا ينطبق على {staff}. نعرض موظفين آخرين في {branch} ينطبق عليهم.',
        'qbr.genderMismatch.emptyTitle': 'لا يوجد موظف في {branch} ينطبق عليه إعداد الجنس في {service}.',
        'qbr.genderMismatch.emptyHint': 'غيّر الإعداد من «منشأتي» ← «الخدمات» ← «{service}»، أو أضف موظفين ينطبق عليهم.',
        'qbr.conflict.toast': 'حُجز هذا الوقت للتو. يُرجى اختيار وقت آخر.',
        'qbr.conflict.emptyTitle': 'لا يوجد وقت متاح لدى {staff} لـ{service} في حدود الساعة {time}.',
        'qbr.conflict.emptyHint': 'جرّب وقتًا آخر، أو اختر موظفًا آخر.',
        'qbr.noPricing.toast': 'لا يوجد سعر لـ{service} بعد. أضف سعرًا من «منشأتي» ← «الخدمات» أولًا.',
        'qbr.noPricing.emptyTitle': 'لا يوجد سعر لـ{service} بعد.',
        'qbr.noPricing.emptyHint': 'أضف سعرًا واحدًا على الأقل من «منشأتي» ← «الخدمات» ← «تعديل»، ثم عُد إلى هنا.',
        'qbr.inPast.toast': 'لا يمكن الحجز في وقت مضى. اختر الوقت الحالي أو وقتًا قادمًا.',
        'qbr.inPast.emptyTitle': 'لا توجد أوقات متاحة في التواريخ السابقة.',
        'qbr.inPast.emptyHint': 'اختر اليوم أو تاريخًا قادمًا.',
        'qbr.unknown.toast': 'تعذّر إتمام هذا الحجز. يُرجى المحاولة مرة أخرى.',
        'qbr.unknown.emptyTitle': 'لا توجد أوقات متاحة تطابق اختياراتك.',
        'qbr.unknown.emptyHint': 'جرّب تاريخًا أو وقتًا آخر.',
        'cal.legend.label': 'حالات الحجز',
        'cal.legend.mobileHint': '📱 = حُجز عبر تطبيق Devanza',
        'cal.week.bookAt': 'احجز الساعة {time}',
        'cal.week.showAll': 'عرض جميع الحجوزات ({count}) في هذا الوقت',
        'cal.week.more': '+{count} أخرى',
        'cal.week.allAt': 'جميع الحجوزات ({count}) الساعة {time}',
        'cal.week.oneBooking': 'حجز واحد',
        'cal.week.nBookings': 'عدد الحجوزات: {count}',
        'cal.week.close': 'إغلاق',
        'cal.week.bookNewAt': '+ حجز جديد الساعة {time}',
        'cs.title': 'إعدادات التقويم',
        'cs.subtitle': 'اختر طريقة عمل التقويم وحالات الحجز التي يستخدمها صالونك.',
        'cs.loadError': 'تعذّر تحميل إعدادات التقويم.',
        'cs.retry': 'إعادة المحاولة',
        'cs.time.title': 'إعدادات الوقت',
        'cs.time.subtitle': 'طريقة عمل الفترات الزمنية والحجوزات في التقويم.',
        'cs.overlap.label': 'السماح بالحجوزات المتداخلة',
        'cs.overlap.help': 'عند التفعيل، يمكنك حجز موعدين للموظف نفسه في الوقت نفسه (مثل أم وابنتها معًا). عند الإيقاف، لا يُسمح بحجز يتداخل مع حجز آخر.',
        'cs.backdating.label': 'السماح بالحجز في وقت سابق',
        'cs.backdating.help': 'عند التفعيل، يمكنك إضافة حجز لوقت مضى، مثلًا لتسجيل زيارة بدون موعد بعد حدوثها. عند الإيقاف، يمكن الحجز في الوقت الحالي والأوقات القادمة فقط.',
        'cs.weekStart': 'يبدأ الأسبوع يوم',
        'cs.day.sunday': 'الأحد',
        'cs.day.monday': 'الاثنين',
        'cs.day.saturday': 'السبت',
        'cs.timeSlot': 'مدة الفترة الزمنية',
        'cs.minutes': '{n} د',
        'cs.timeFormat': 'تنسيق الوقت',
        'cs.timeFormat.12': '12 ساعة (2:30 م)',
        'cs.timeFormat.24': '24 ساعة (14:30)',
        'cs.status.title': 'حالات الحجز',
        'cs.status.subtitle': 'أوقف الخطوات التي لا يتابعها صالونك. أسماء الحالات وألوانها موحّدة في Devanza ليقرأ الجميع الحجوزات بالطريقة نفسها.',
        'cs.status.hint.booked': 'طلبات الحجز الجديدة بانتظار ردّك، والحجوزات التي أكّدتها.',
        'cs.status.hint.arrived': 'وصل العميل إلى الصالون.',
        'cs.status.hint.started': 'الخدمة قيد التنفيذ.',
        'cs.status.hint.completed': 'انتهت الزيارة.',
        'cs.status.hint.noShow': 'لم يحضر العميل.',
        'cs.status.hint.cancelled': 'أُلغي الحجز أو رُفض.',
        'cs.status.alwaysOn': 'مفعّلة دائمًا.',
        'cs.status.note': 'إيقاف حالة يخفيها من الإجراءات المتاحة على الحجوزات الجديدة. الحجوزات الموجودة في تلك الحالة تحتفظ بها وتظل ظاهرة في التقويم.',
        'cs.discard': 'تجاهل التغييرات',
        'cs.save': 'حفظ الإعدادات',
        'cs.saving': 'جارٍ الحفظ…',
        'cs.toast.saved': 'تم حفظ إعدادات التقويم.',
        'cs.toast.saveFailed': 'تعذّر حفظ إعدادات التقويم. يُرجى المحاولة مرة أخرى.',
    },
};
